// Public pages: centered header, home, portfolio, section, about, contact.

import React from "react";
import { imageUrl } from "./api.js";
import { Signature, Placeholder, WorkMedia, Lightbox } from "./components.jsx";

export function CenteredHeader({ route, go }) {
  const links = [
    { id: "portfolio", label: "Portfolio" },
    { id: "about",     label: "About" },
    { id: "contact",   label: "Contact" },
  ];
  const active = (id) => {
    if (id === "portfolio") return route.name === "portfolio" || route.name === "section";
    return route.name === id;
  };
  return (
    <header className="centered-head">
      <Signature size={56} onClick={() => go("#/")} className="centered-sig" />
      <nav className="centered-nav">
        {links.map((l) => (
          <span
            key={l.id}
            className={"centered-nav-link" + (active(l.id) ? " is-active" : "")}
            onClick={() => go("#/" + l.id)}
          >
            {l.label}
          </span>
        ))}
      </nav>
    </header>
  );
}

export function MinimalFooter() {
  // Admin entry is intentionally not linked from the public site —
  // visit /admin directly (Cloudflare Access prompts for OTP).
  return (
    <footer className="centered-foot">
      <div>© Chris Dicker</div>
    </footer>
  );
}

export function Home({ state, go }) {
  const allWorks = state.sections.flatMap((s) => s.draft ? [] : s.works.map((w) => ({ ...w, _sec: s })));
  const featured = allWorks.find((w) => w.id === state.featuredWorkId) || allWorks[0];

  if (state.loading) return <div className="home-stage"/>;

  return (
    <section className="home-stage">
      {!featured ? (
        <div className="empty" style={{ maxWidth: 520, margin: "60px auto" }}>
          No works yet.
        </div>
      ) : (() => {
        // Compute width = height × ratio explicitly. Inside a flex parent,
        // width:auto + aspect-ratio collapses to 0 (MDN-documented). Setting
        // both width and height in CSS sidesteps it entirely.
        const aspect = featured.image?.aspect || "4/5";
        const [aw, ah] = aspect.split("/").map(Number);
        const ratio = (aw || 4) / (ah || 5);
        const H = "min(60vh, 580px, calc(100vh - 340px))";
        const W = `min(calc(${H} * ${ratio}), 92vw, calc(100% - 32px))`;
        return (
          <div className="home-mat" onClick={() => go("#/portfolio")} title="View portfolio">
            <div className="home-mat-inner">
              <div style={{ width: W, height: H, display: "flex" }}>
                {featured.image?.kind === "upload"
                  ? <img src={imageUrl(featured.image.key)} alt={featured.title}
                         style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
                  : <Placeholder
                      seed={featured.image?.seed || 0}
                      aspect={aspect}
                      label={featured.image?.label || "painting"}
                    />}
              </div>
            </div>
          </div>
        );
      })()}
    </section>
  );
}

export function Portfolio({ state, go }) {
  const sections = state.sections.filter((s) => !s.draft);
  return (
    <div className="page">
      <h1 className="page-title">Portfolio</h1>
      <div className="page-sub">Selected works · 2018 — present</div>
      {sections.length === 0 ? (
        <div className="empty">No published sections yet.</div>
      ) : (
        <div className="section-grid">
          {sections.map((sec) => {
            const cover = sec.works.find((w) => w.id === sec.coverWorkId) || sec.works[0];
            return (
              <div key={sec.id} className="section-card" onClick={() => go(`#/section/${sec.id}`)}>
                <div className="sc-cover">
                  {cover
                    ? <WorkMedia work={cover} fit="cover" showLabel={false} />
                    : <Placeholder seed={0} aspect="4/3" label="empty" />}
                </div>
                <div className="sc-meta">
                  <span className="sc-title">{sec.title}</span>
                  <span className="sc-count">{sec.works.length} {sec.works.length === 1 ? "work" : "works"}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function SectionPage({ state, sid, go }) {
  const sec = state.sections.find((s) => s.id === sid);
  const [lbIdx, setLbIdx] = React.useState(null);
  if (!sec) return (
    <div className="page">
      <div className="empty">Section not found. <a onClick={() => go("#/portfolio")} style={{ cursor: "pointer", color: "var(--accent)" }}>Back to portfolio</a></div>
    </div>
  );

  return (
    <div className="section-page">
      <div className="section-head">
        <div className="section-kicker"><span style={{ cursor: "pointer" }} onClick={() => go("#/portfolio")}>← Portfolio</span></div>
        <h1 className="section-title">{sec.title}</h1>
        {sec.blurb && <p className="section-desc">{sec.blurb}</p>}
      </div>
      {sec.works.length === 0 ? (
        <div className="empty">No works in this section.</div>
      ) : (
        <div className="works">
          {sec.works.map((w, i) => (
            <article key={w.id} className="work" onClick={() => setLbIdx(i)}>
              <div className="work-img-wrap">
                <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                  {w.image?.kind === "upload"
                    ? <img className="work-img" src={imageUrl(w.image.key)} alt={w.title} />
                    : <div style={{ width: "min(100%, 820px)", aspectRatio: w.image?.aspect || "4/3", display: "flex" }}>
                        <Placeholder seed={w.image?.seed || 0} aspect={w.image?.aspect || "4/3"} label={w.image?.label || "painting"} />
                      </div>}
                </div>
              </div>
              <div className="work-meta">
                <div className="wm-left">
                  <span className="wm-title">{w.title}</span>
                  {w.year && <span className="wm-year">{w.year}</span>}
                  {w.medium && <span className="wm-medium">{w.medium}</span>}
                  {w.dimensions && <span className="wm-medium">{w.dimensions}</span>}
                </div>
                <span className="work-num">№ {String(i + 1).padStart(2, "0")}</span>
              </div>
            </article>
          ))}
        </div>
      )}
      {lbIdx !== null && (
        <Lightbox
          works={sec.works}
          index={lbIdx}
          onClose={() => setLbIdx(null)}
          onIndex={setLbIdx}
        />
      )}
    </div>
  );
}

export function About({ state }) {
  return (
    <div className="page">
      <h1 className="page-title">{state.bio.headline}</h1>
      <div className="page-sub">{state.bio.location}</div>
      <div className="prose">
        {state.bio.body.split(/\n\n+/).map((p, i) => <p key={i}>{p}</p>)}
      </div>
    </div>
  );
}

export function Contact({ state }) {
  const c = state.contact;
  return (
    <div className="page">
      <h1 className="page-title">Contact</h1>
      <div className="page-sub">{c.inquiries}</div>
      <div className="contact-grid">
        <div>
          <div className="contact-block"><div className="ck">Email</div><div className="cv"><a href={`mailto:${c.email}`}>{c.email}</a></div></div>
          {c.instagram && <div className="contact-block" style={{ marginTop: 24 }}><div className="ck">Instagram</div><div className="cv">{c.instagram}</div></div>}
          {c.address && <div className="contact-block" style={{ marginTop: 24 }}><div className="ck">Studio</div><div className="cv">{c.address}</div></div>}
        </div>
        <div>
          {c.representation && <div className="contact-block"><div className="ck">Representation</div><div className="cv">{c.representation}</div></div>}
        </div>
      </div>
    </div>
  );
}
